import { Monitor, Moon, Sun } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { ThemePreference } from '@shared/types'
import { cn } from '@/lib/cn'
import { useThemeStore } from '@/stores/useThemeStore'

const OPTIONS: Array<{ value: ThemePreference; label: string; icon: LucideIcon }> = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'System', icon: Monitor }
]

interface ThemeToggleProps {
  className?: string
}

export default function ThemeToggle({ className }: ThemeToggleProps): React.ReactElement {
  const theme = useThemeStore((state) => state.theme)
  const resolved = useThemeStore((state) => state.resolved)
  const setTheme = useThemeStore((state) => state.setTheme)

  return (
    <div
      role="group"
      aria-label="Colour theme"
      className={cn(
        'inline-flex items-center gap-0.5 rounded-control border border-line bg-sunken p-0.5',
        className
      )}
    >
      {OPTIONS.map(({ value, label, icon: Icon }) => {
        const active = theme === value

        return (
          <button
            key={value}
            type="button"
            onClick={() => setTheme(value)}
            aria-pressed={active}
            aria-label={
              value === 'system' ? `Match system theme (currently ${resolved})` : `${label} theme`
            }
            className={cn(
              'grid h-7 w-7 place-items-center rounded-[0.45rem] transition-colors',
              active
                ? 'bg-surface text-fg shadow-sheet'
                : 'text-fg-subtle hover:bg-surface/60 hover:text-fg-muted'
            )}
          >
            <Icon className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        )
      })}
    </div>
  )
}

export { ThemeToggle }
